<i18n>
{
  "cl": {
    "refused": "Rechazado por elegibilidad"
  }
}
</i18n>
<template>
  <div>
    <b-table
      :data="data"
      class="evaluation-table"
      :draggable="false"
      detailed
      show-detail-icon>
      <b-table-column v-slot="{ row }" field="index" label="" class="featured-column">
        <span class="has-text-primary">Evaluación {{ row.index }}</span>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="applicationDate" label="Fecha">
        {{ row.applicationDate }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="applicant" label="Solicitante">
        {{ row.applicant }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="creditAmount" label="Monto del crédito">
        {{ formatNumberByCountry(row.creditAmount) }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="commercialValue" label="Monto del vivienda">
        {{ formatNumberByCountry(row.commercialValue) }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="result" label="Resultado">
        <b-tag
          :type="`${getEvaluationResult(row).tagClass} is-light`"
          class="result-icon-list"
          rounded>
          {{ getEvaluationResult(row).evaluationResultLabel }}
        </b-tag>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="simulation" label="Simulación">
        <router-link
          data-cy="open-simulation"
          :to="getSimulationLink(row)">
          Ver
        </router-link>
      </b-table-column>
      <b-table-column field="document" label="Reporte">
        --
      </b-table-column>
      <template #detail="{ row }">
        <div class="columns">
          <div class="column is-6">
            <div v-if="showExplanations(row)" class="explanations column">
              <p class="has-text-weight-medium">La simulación no pudo ser pre-aprobada porque:</p>
              <ul class="fa-ul">
                <li
                  v-for="(reason, index) in row.explanations"
                  :key="index">
                  <span class="fa-li">
                    <i class="fas fa-circle-arrow-right has-text-primary" />
                  </span>
                  {{ reason }}
                </li>
              </ul>
            </div>
            <div v-if="showMessages(row)" class="messages column">
              <p class="has-text-weight-medium">Gestiones a realizar:</p>
              <ul class="fa-ul">
                <li
                  v-for="(task, index) in row.messages"
                  :key="index">
                  <span class="fa-li">
                    <i class="fas fa-circle-arrow-right has-text-primary" />
                  </span>
                  {{ task }}
                </li>
              </ul>
            </div>
          </div>
          <div class="column is-6">
            <b-collapse
              :open="false"
              position="is-bottom">
              <template #trigger="props">
                <a
                  aria-controls="contentIdForA11y4"
                  :aria-expanded="props.open">
                  <b-icon :icon="!props.open ? 'caret-down' : 'caret-up'" />
                  {{ !props.open ? 'Logs...' : 'Ocultar logs...' }}
                </a>
              </template>
              <ul class="logs">
                <li
                  v-for="(error, index) in row.reasonsError"
                  :key="index"
                  class="is-size-7">
                  {{ error }}
                </li>
              </ul>
            </b-collapse>
          </div>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { BffEvaluationsClient } from '@/application/services/api-clients/bff-evaluations-client';
import { evaluationsBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';
import { formatNumberByCountry } from '@/infrastructure/format';

export default {
  name: 'EvaluationTable',
  props: {
    idNumber: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      data: [],
      selected: {}
    };
  },
  async mounted() {
    await this.fetchEvaluationData();
    this.selectedEvaluation();
  },
  methods: {
    selectedEvaluation() {
      const foundRow = this.data.find(
        row => row.automaticEvaluationId === this.$route.params.evaluationId
      );
      if (foundRow) {
        this.selected = foundRow;
      }
    },
    getSimulationLink(row) {
      return {
        name: 'evaluation-simulation',
        params: {
          evaluationId: row.automaticEvaluationId
        }
      };
    },
    async fetchEvaluationData() {
      try {
        const evaluationsClient = new BffEvaluationsClient(evaluationsBackend, authenticationService);
        const evaluations = await evaluationsClient.getEvaluations(this.idNumber);
        this.data = evaluations.data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error al obtener las evaluaciones:', error);
      }
    },
    showExplanations(evaluationItem) {
      return !!(evaluationItem.result !== 'Approved'
        && evaluationItem.explanations.length > 0);
    },
    showMessages(evaluationItem) {
      return !!(evaluationItem.result !== 'Approved'
        && evaluationItem.messages?.length > 0);
    },
    formatNumberByCountry(number, usePrefix = true, currencyType = 'primary') {
      if (number === null || number === undefined) {
        return '--';
      }
      return formatNumberByCountry({ number,
        countryIn: 'cl',
        usePrefix,
        currencyType });
    },
    getEvaluationResult({ result, eligibility }) {
      const defaultParams = {
        tagClass: 'is-warning',
        evaluationResultLabel: 'Revisión pendiente',
        evaluationResultIcon: 'times',
        evaluationDetailResultIcon: 'times-circle'
      };
      if (result === 'Approved') {
        return {
          tagClass: 'is-primary',
          evaluationResultLabel: 'Sin revisión pendiente',
          evaluationResultIcon: 'check',
          evaluationDetailResultIcon: 'circle-check'
        };
      }
      if (result === 'Refused' && eligibility === 'Refused') {
        return {
          ...defaultParams,
          tagClass: 'is-danger',
          evaluationResultLabel: this.$t('refused')
        };
      }
      return defaultParams;
    }
  }
};
</script>

<style>
.evaluation-table .table {
  border: 1px solid #e5e5e5 !important;
  font-size: 14px;
  border-radius: 10px;
  border-collapse: separate;
  overflow: hidden;
  margin-bottom: 1rem;
}

.evaluation-table .table th {
  background-color: #f4f4f4;
  border: none;
}

.evaluation-table .table tr.detail {
  background-color: #FFFFFF;
  box-shadow: none;
}

.evaluation-table .table tr td,
.evaluation-table .table tr.detail td {
  border: none;
}

.evaluation-table .table tr td {
  border-top: 1px solid #e5e5e5;
  vertical-align: middle;
}

.evaluation-table .fa-ul {
  margin-left: 21px;
}

.evaluation-table ul.logs {
  list-style-type: disc;
  padding-left: 20px;
  color: #333333;
  font-size: 14px;
}

.evaluation-table .result-icon-list {
  padding: 0 5px !important;
}

@media only screen and (max-width: 768px) {
  .evaluation-table .b-table .table {
    border: none;
  }

  .evaluation-table .b-table .table thead {
    display: none !important;
  }

  .evaluation-table .b-table .table tr {
    box-shadow: none !important;
    border: 1px solid #dbdbdb !important;
    border-radius: 10px;
  }

  .evaluation-table .b-table .table-wrapper.has-mobile-cards tr:not(.detail):not(.is-empty):not(.table-footer) td {
    border: none;
  }
}
</style>
